import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "templates-page" };
const _hoisted_2 = { class: "templates-container" };
const _hoisted_3 = { class: "item-info" };
const _hoisted_4 = { class: "item-name" };
const _hoisted_5 = { class: "item-subtitle" };
const _hoisted_6 = { key: 0 };
const _hoisted_7 = { class: "section-header" };
const _hoisted_8 = { class: "form-group" };
const _hoisted_9 = { class: "form-group" };
const _hoisted_10 = { class: "tabs" };
const _hoisted_11 = ["disabled"];
const _hoisted_12 = {
    key: 0,
    class: "merge-fields"
};
const _hoisted_13 = ["for"];
const _hoisted_14 = ["id", "onUpdate:modelValue", "placeholder"];
const _hoisted_15 = ["srcdoc"];
const _hoisted_16 = {
    key: 1,
    class: "empty-state-section"
};
const _hoisted_17 = { class: "form-group" };
const _hoisted_18 = ["for"];
const _hoisted_19 = ["id", "onUpdate:modelValue"];
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { EditorView, basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import { html } from '@codemirror/lang-html';
import { oneDark } from '@codemirror/theme-one-dark';
import { findPlaceholders, applyPlaceholders } from '@/utils/mergeFields';
import { useAdmin } from '@/composables/useAdmin';
import MGDialog from '@/components/elements/MGDialog.vue';
import MGButton from '@/components/elements/MGButton.vue';
import AdminListSection from '@/admin/components/AdminListSection.vue';
import AdminHeaderActions from '@/admin/components/AdminHeaderActions.vue';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EmailTemplatesPage',
    setup(__props) {
        const admin = useAdmin();
        const selectedTemplate = ref(null);
        const editorContainer = ref(null);
        const editor = ref(null);
        const saving = ref(false);
        const tab = ref('html');
        const showDeleteDialog = ref(false);
        const showValidationToast = ref(false);
        const validationMessage = ref('');
        const toastColour = ref('error');
        const showSendDialog = ref(false);
        const sendTo = ref('');
        const sending = ref(false);
        // Values for the preview's merge fields, keyed by token name. Not saved with the template.
        const previewVars = ref({});
        const placeholders = computed(() => findPlaceholders(selectedTemplate.value?.htmlBody || ''));
        const previewHtml = computed(() => applyPlaceholders(selectedTemplate.value?.htmlBody || '', previewVars.value));
        const templates = computed(() => admin.adminState.emailTemplates);
        const templatesLoading = computed(() => admin.adminState.emailTemplatesLoading);
        // Same shape as PolicySectionsPage's editor: one instance, rebuilt per document, with an
        // update listener writing back to the model the save call already reads.
        function createEditorInstance(content) {
            const container = editorContainer.value;
            if (!container)
                return;
            if (editor.value) {
                editor.value.destroy();
                editor.value = null;
            }
            container.innerHTML = '';
            const updateListener = EditorView.updateListener.of((update) => {
                if (update.docChanged && selectedTemplate.value) {
                    selectedTemplate.value.htmlBody = update.state.doc.toString();
                }
            });
            editor.value = new EditorView({
                state: EditorState.create({
                    doc: content,
                    extensions: [
                        basicSetup,
                        html(),
                        oneDark,
                        updateListener,
                        EditorView.theme({
                            '&': { height: '100%' },
                            '.cm-scroller': { overflow: 'auto' },
                            '.cm-content': { fontFamily: 'monospace' }
                        })
                    ]
                }),
                parent: container
            });
        }
        async function openInEditor(content) {
            tab.value = 'html';
            await nextTick();
            createEditorInstance(content);
        }
        function selectTemplate(template) {
            // edit a copy so cancelling/refreshing doesn't mutate the list
            selectedTemplate.value = { ...template };
            openInEditor(template.htmlBody || '');
        }
        function newTemplate() {
            selectedTemplate.value = { name: '', subject: '', htmlBody: '' };
            openInEditor('');
        }
        function saveTemplate() {
            const template = selectedTemplate.value;
            if (!template)
                return;
            if (!template.name?.trim()) {
                showToast('Please enter a template name', 'error');
                return;
            }
            saving.value = true;
            const done = (result) => {
                saving.value = false;
                // The server echoes the saved row back. Only reseat the document if it actually
                // differs, so a save never moves the caret out from under whoever is typing.
                if (result?.id) {
                    selectedTemplate.value = { ...result };
                    const body = result.htmlBody || '';
                    if (editor.value && editor.value.state.doc.toString() !== body) {
                        createEditorInstance(body);
                    }
                }
            };
            if (template.id) {
                admin.updateEmailTemplate(template.id, template, done);
            }
            else {
                admin.createEmailTemplate(template, done);
            }
        }
        function showToast(message, colour) {
            validationMessage.value = message;
            toastColour.value = colour;
            showValidationToast.value = true;
        }
        function sendEmail() {
            const to = sendTo.value.trim();
            const templateData = {};
            placeholders.value.forEach((key) => { templateData[key] = previewVars.value[key] || ''; });
            sending.value = true;
            admin.sendEmail({ to, templateName: selectedTemplate.value.name, templateData }, () => {
                sending.value = false;
                showSendDialog.value = false;
                showToast('Email sent to ' + to, 'success');
            }, (error) => {
                sending.value = false;
                showToast('Failed to send email: ' + error, 'error');
            });
        }
        function confirmDelete() {
            admin.deleteEmailTemplate(selectedTemplate.value.id, () => {
                selectedTemplate.value = null;
                showDeleteDialog.value = false;
            });
        }
        onMounted(() => {
            admin.getEmailTemplates();
        });
        onBeforeUnmount(() => {
            if (editor.value) {
                editor.value.destroy();
                editor.value = null;
            }
        });
        return (_ctx, _cache) => {
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_snackbar = _resolveComponent("v-snackbar");
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createVNode(AdminListSection, {
                            title: "Email Templates",
                            items: templates.value,
                            "selected-id": selectedTemplate.value?.id,
                            loading: templatesLoading.value,
                            "empty-message": "No email templates yet",
                            "search-fields": ['name', 'subject'],
                            "show-new-button": true,
                            "new-button-text": "New Template",
                            onSelect: selectTemplate,
                            onRefresh: _cache[0] || (_cache[0] = ($event) => (_unref(admin).getEmailTemplates())),
                            onNew: newTemplate
                        }, {
                            item: _withCtx(({ item }) => [
                                _createElementVNode("div", _hoisted_3, [
                                    _createElementVNode("div", _hoisted_4, _toDisplayString(item.name || 'Untitled'), 1),
                                    _createElementVNode("div", _hoisted_5, _toDisplayString(item.subject), 1)
                                ])
                            ]),
                            _: 1
                        }, 8, ["items", "selected-id", "loading"]),
                        _createElementVNode("div", {
                            class: _normalizeClass(["editor-section", { 'is-preview': tab.value === 'preview' }])
                        }, [
                            (selectedTemplate.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                    _createElementVNode("div", _hoisted_7, [
                                        _createElementVNode("h2", null, _toDisplayString(selectedTemplate.value.name || 'New Template'), 1),
                                        _createVNode(AdminHeaderActions, {
                                            loading: saving.value,
                                            "has-id": !!selectedTemplate.value.id,
                                            onSave: saveTemplate,
                                            onDelete: _cache[1] || (_cache[1] = ($event) => (showDeleteDialog.value = true)),
                                            onNew: newTemplate
                                        }, null, 8, ["loading", "has-id"])
                                    ]),
                                    _createElementVNode("div", _hoisted_8, [
                                        _cache[14] || (_cache[14] = _createElementVNode("label", null, "Name", -1)),
                                        _withDirectives(_createElementVNode("input", {
                                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((selectedTemplate.value.name) = $event)),
                                            type: "text",
                                            class: "form-input",
                                            placeholder: "e.g., EmailAddressVerification"
                                        }, null, 512), [
                                            [_vModelText, selectedTemplate.value.name]
                                        ])
                                    ]),
                                    _createElementVNode("div", _hoisted_9, [
                                        _cache[15] || (_cache[15] = _createElementVNode("label", null, "Subject", -1)),
                                        _withDirectives(_createElementVNode("input", {
                                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((selectedTemplate.value.subject) = $event)),
                                            type: "text",
                                            class: "form-input",
                                            placeholder: "e.g., Here is your Money Guided Code!"
                                        }, null, 512), [
                                            [_vModelText, selectedTemplate.value.subject]
                                        ])
                                    ]),
                                    _createElementVNode("div", _hoisted_10, [
                                        _createElementVNode("button", {
                                            class: _normalizeClass(["action-btn secondary", { selected: tab.value === 'html' }]),
                                            onClick: _cache[4] || (_cache[4] = ($event) => (tab.value = 'html'))
                                        }, "HTML", 2),
                                        _createElementVNode("button", {
                                            class: _normalizeClass(["action-btn secondary", { selected: tab.value === 'preview' }]),
                                            onClick: _cache[5] || (_cache[5] = ($event) => (tab.value = 'preview'))
                                        }, "Preview", 2),
                                        _createElementVNode("button", {
                                            class: "action-btn secondary send-btn",
                                            disabled: !selectedTemplate.value.id,
                                            onClick: _cache[6] || (_cache[6] = ($event) => (showSendDialog.value = true))
                                        }, "✉️ Send", 8, _hoisted_11)
                                    ]),
                                    (tab.value === 'preview' && placeholders.value.length)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(placeholders.value, (key) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                    key: key,
                                                    class: "merge-field"
                                                }, [
                                                    _createElementVNode("label", {
                                                        for: 'mf-' + key
                                                    }, _toDisplayString(key), 9, _hoisted_13),
                                                    _withDirectives(_createElementVNode("input", {
                                                        id: 'mf-' + key,
                                                        "onUpdate:modelValue": ($event) => ((previewVars.value[key]) = $event),
                                                        type: "text",
                                                        class: "form-input",
                                                        placeholder: '{{' + key + '}}'
                                                    }, null, 8, _hoisted_14), [
                                                        [_vModelText, previewVars.value[key]]
                                                    ])
                                                ]));
                                            }), 128))
                                        ]))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("div", {
                                        class: _normalizeClass(["editor-panes", { 'is-preview': tab.value === 'preview' }])
                                    }, [
                                        _createElementVNode("div", {
                                            ref_key: "editorContainer",
                                            ref: editorContainer,
                                            class: _normalizeClass(["html-editor", { 'is-hidden': tab.value !== 'html' }])
                                        }, null, 2),
                                        _createElementVNode("iframe", {
                                            class: _normalizeClass(["preview-frame", { 'is-hidden': tab.value !== 'preview' }]),
                                            sandbox: "",
                                            srcdoc: previewHtml.value
                                        }, null, 10, _hoisted_15)
                                    ], 2)
                                ]))
                                : (_openBlock(), _createElementBlock("div", _hoisted_16, _cache[16] || (_cache[16] = [
                                    _createElementVNode("p", null, "Select a template to edit", -1)
                                ])))
                        ], 2)
                    ])
                ]),
                _createVNode(MGDialog, {
                    modelValue: showDeleteDialog.value,
                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((showDeleteDialog).value = $event)),
                    title: "Delete Template",
                    "max-width": "300px",
                    persistent: true
                }, {
                    content: _withCtx(() => [
                        _createElementVNode("p", null, "Are you sure you want to delete \"" + _toDisplayString(selectedTemplate.value?.name) + "\"?", 1)
                    ]),
                    actions: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: "Delete",
                            onClick: confirmDelete,
                            stacked: true
                        }),
                        _createVNode(MGButton, {
                            text: "Cancel",
                            outline: true,
                            onClick: _cache[7] || (_cache[7] = ($event) => (showDeleteDialog.value = false)),
                            stacked: true
                        })
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(MGDialog, {
                    modelValue: showSendDialog.value,
                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((showSendDialog).value = $event)),
                    title: "Send Email",
                    "max-width": "400px",
                    persistent: true
                }, {
                    content: _withCtx(() => [
                        _createElementVNode("div", _hoisted_17, [
                            _cache[17] || (_cache[17] = _createElementVNode("label", { for: "send-to" }, "To", -1)),
                            _withDirectives(_createElementVNode("input", {
                                id: "send-to",
                                "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((sendTo).value = $event)),
                                type: "email",
                                class: "form-input",
                                placeholder: "name@example.com"
                            }, null, 512), [
                                [_vModelText, sendTo.value]
                            ])
                        ]),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(placeholders.value, (key) => {
                            return (_openBlock(), _createElementBlock("div", {
                                key: key,
                                class: "form-group"
                            }, [
                                _createElementVNode("label", {
                                    for: 'send-' + key
                                }, _toDisplayString(key), 9, _hoisted_18),
                                _withDirectives(_createElementVNode("input", {
                                    id: 'send-' + key,
                                    "onUpdate:modelValue": ($event) => ((previewVars.value[key]) = $event),
                                    type: "text",
                                    class: "form-input"
                                }, null, 8, _hoisted_19), [
                                    [_vModelText, previewVars.value[key]]
                                ])
                            ]));
                        }), 128))
                    ]),
                    actions: _withCtx(() => [
                        _createVNode(MGButton, {
                            text: "Send",
                            onClick: sendEmail,
                            disabled: sending.value || !sendTo.value.trim(),
                            stacked: true
                        }, null, 8, ["disabled"]),
                        _createVNode(MGButton, {
                            text: "Cancel",
                            outline: true,
                            onClick: _cache[10] || (_cache[10] = ($event) => (showSendDialog.value = false)),
                            stacked: true
                        })
                    ]),
                    _: 1
                }, 8, ["modelValue"]),
                _createVNode(_component_v_snackbar, {
                    modelValue: showValidationToast.value,
                    "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event) => ((showValidationToast).value = $event)),
                    timeout: 4000,
                    location: "bottom",
                    color: toastColour.value
                }, {
                    actions: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                            icon: "mdi-close",
                            size: "small",
                            onClick: _cache[12] || (_cache[12] = ($event) => (showValidationToast.value = false))
                        })
                    ]),
                    default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(validationMessage.value) + " ", 1)
                    ]),
                    _: 1
                }, 8, ["modelValue", "color"])
            ], 64));
        };
    }
});
